/**
 * Block 12 Hotfix-2C Phase C5 Step 10 — useCalculatorStore (Zustand).
 *
 * In-memory anketa draft store keyed by `CalculatorAnketaType`. Survives
 * client-side navigation в пределах SPA сессии (cross-route, cross-block
 * deeplinks типа `<FindPriceButton>`) без re-mount пересоздания state.
 *
 * **Roles:**
 *   - Layer 1 (this module): fast in-memory dictionary, lookup synchronous.
 *   - Layer 2 (`calculatorIdbSync` Step 11): durable IDB-backed mirror — F5
 *     refresh + browser restart survive. Async I/O.
 *   - Layer 3 wiring (`AnketaFormContainer` Step 12): hydration cascade
 *     Zustand → IDB → blank; debounced 300ms save → setDraft + saveDraftToIdb.
 *
 * **Why per-anketaType key (а не projectId UUID):** Bug #2 Audit Round 3 —
 * пользователь кликает «Найти и узнать цену» (FindPriceButton без projectId
 * tracking) → переходит в каталог → «Назад» → URL свежий без `?draftId=`,
 * AnketaFormContainer создаёт новый UUID. Без anketaType-keyed persistence
 * данные теряются. С persistence — hydration по anketaType восстанавливает
 * последний черновик независимо от того, какой projectId сейчас активен.
 *
 * **Не путать с `useAnketaPersist`** — тот пишет в `projects` IDB store по
 * UUID (для `?draftId=` resume flow). Здесь — отдельный слой по anketaType
 * (для cross-navigation Bug #2). Two persistence layers co-exist.
 *
 * **No persist middleware:** Zustand persist middleware → localStorage; для
 * AnketaState (большой shape с params + ~10 maps) IDB подходит лучше
 * (отдельный модуль Step 11). Этот store — только in-memory.
 *
 * Reference: Block_12_hotfix2C_2026-05-17_start.md §6 (Phase C5 Step 10).
 */

import { create } from 'zustand';
import type { CalculatorAnketaType } from '@pomoshnik/shared';
import type { AnketaState } from '../types';

export interface CalculatorDraftStore {
  /**
   * Map anketaType → последний AnketaState. `Partial` потому что не все типы
   * могут быть заполнены одновременно (пользователь обычно работает с одним).
   */
  drafts: Partial<Record<CalculatorAnketaType, AnketaState>>;

  /** Перезаписывает черновик для конкретного anketaType. Sync. */
  setDraft: (anketaType: CalculatorAnketaType, state: AnketaState) => void;
  /** Lookup черновик. `undefined` если нет. Sync. */
  getDraft: (anketaType: CalculatorAnketaType) => AnketaState | undefined;
  /** Удаляет черновик для типа (e.g. после save→backend). Sync. */
  clearDraft: (anketaType: CalculatorAnketaType) => void;
  /** Полная очистка (e.g. logout). Sync. */
  clearAll: () => void;
}

export const useCalculatorStore = create<CalculatorDraftStore>((set, get) => ({
  drafts: {},

  setDraft: (anketaType, state) => set((s) => ({ drafts: { ...s.drafts, [anketaType]: state } })),

  getDraft: (anketaType) => get().drafts[anketaType],

  clearDraft: (anketaType) =>
    set((s) => {
      if (!(anketaType in s.drafts)) return s;
      const next = { ...s.drafts };
      delete next[anketaType];
      return { drafts: next };
    }),

  clearAll: () => set({ drafts: {} }),
}));

/**
 * Тестовый утилит — сбрасывает store. `beforeEach` test cleanup чтобы
 * между тестами не было утечек state. Не использовать в production code.
 */
export function resetCalculatorStoreForTests(): void {
  useCalculatorStore.setState({ drafts: {} });
}
